export type AtlasSearch = Record<string, string | string[]>;

type QueryValue = string | number | boolean | null | undefined;

export type AtlasHref =
  | string
  | {
      pathname?: string;
      query?: Record<string, QueryValue | readonly QueryValue[]>;
      hash?: string;
    };

/** Keep existing URLSearchParams links readable, including repeated values. */
export function parseAtlasSearch(search: string): AtlasSearch {
  const result: AtlasSearch = {};
  for (const [key, value] of new URLSearchParams(search)) {
    const previous = Object.hasOwn(result, key) ? result[key] : undefined;
    Object.defineProperty(result, key, {
      configurable: true,
      enumerable: true,
      writable: true,
      value:
        previous === undefined
          ? value
          : Array.isArray(previous)
            ? [...previous, value]
            : [previous, value],
    });
  }
  return result;
}

export function stringifyAtlasSearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    const values = Array.isArray(value) ? value : [value];
    for (const item of values) {
      if (item !== undefined && item !== null) params.append(key, String(item));
    }
  }
  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}

export function formatAtlasHref(href: AtlasHref): string {
  if (typeof href === "string") return href;
  const hash = href.hash ? `#${href.hash.replace(/^#/, "")}` : "";
  return `${href.pathname ?? ""}${stringifyAtlasSearch(href.query ?? {})}${hash}`;
}

/** TanStack receives pathname, search and hash separately for internal links. */
export function atlasNavigationTarget(href: AtlasHref) {
  const formatted = formatAtlasHref(href);
  const hashIndex = formatted.indexOf("#");
  const pathAndSearch = hashIndex < 0 ? formatted : formatted.slice(0, hashIndex);
  const searchIndex = pathAndSearch.indexOf("?");
  return {
    to: (searchIndex < 0 ? pathAndSearch : pathAndSearch.slice(0, searchIndex)) || ".",
    search: formatted.startsWith("#")
      ? true as const
      : parseAtlasSearch(searchIndex < 0 ? "" : pathAndSearch.slice(searchIndex)),
    hash: hashIndex < 0 ? "" : formatted.slice(hashIndex + 1),
  };
}

export function isExternalAtlasHref(href: string): boolean {
  return /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(href);
}
